import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingCart, ExternalLink, Smartphone, Globe, CreditCard, Star, Send } from "lucide-react";
import { api } from "@/lib/api";

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ reviewer_name: "", rating: 5, comment: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    api.getMarketplaceProduct(productId)
      .then(setProduct)
      .catch(() => navigate("/marketplace"))
      .finally(() => setLoading(false));
    api.getReviews(productId).then(setReviews).catch(() => {});
  }, [productId, navigate]);

  const handlePurchase = async (type = "one_time") => {
    setPurchasing(true);
    try {
      const result = await api.createCheckout({
        product_id: product.id,
        purchase_type: type,
        origin_url: window.location.origin,
      });
      window.location.href = result.url;
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to initiate checkout");
      setPurchasing(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setReviewSubmitting(true);
    try {
      await api.createReview({ ...reviewForm, product_id: productId });
      setReviewForm({ reviewer_name: "", rating: 5, comment: "" });
      const updated = await api.getReviews(productId);
      setReviews(updated);
      const updatedProduct = await api.getMarketplaceProduct(productId);
      setProduct(updatedProduct);
    } catch (err) { alert(err.response?.data?.detail || "Failed to submit review"); }
    finally { setReviewSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="product-detail-page">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate("/marketplace")}
          data-testid="product-back"
          className="flex items-center gap-2 text-[#A0A0AB] hover:text-[#00F0FF] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketplace
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-8"
          >
            {product.image_url && (
              <div className="glass-card rounded-3xl overflow-hidden mb-8">
                <img src={product.image_url} alt={product.name} className="w-full h-64 sm:h-80 object-cover" />
              </div>
            )}
            <p className="label-mono mb-3">{product.product_type}</p>
            <h1 data-testid="product-title" className="font-['Unbounded'] text-3xl sm:text-4xl font-black tracking-tighter text-white mb-3">
              {product.name}
            </h1>
            {product.creator?.name && (
              <p className="text-[#A0A0AB] text-sm mb-6">
                by <span className="text-white">{product.creator.company_name || product.creator.name}</span>
              </p>
            )}
            <p className="text-[#A0A0AB] text-base leading-relaxed mb-8">{product.description}</p>

            {product.platforms?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">Platforms</h3>
                <div className="flex gap-3 flex-wrap">
                  {product.platforms.map((p) => (
                    <span key={p} className="flex items-center gap-2 glass-card rounded-xl px-4 py-2 text-sm text-white">
                      {p === "Web" ? <Globe className="w-4 h-4 text-[#00F0FF]" /> : <Smartphone className="w-4 h-4 text-[#00F0FF]" />}
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {product.demo_url && (
              <a href={product.demo_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#00F0FF] hover:text-white transition-colors text-sm">
                <ExternalLink className="w-4 h-4" /> Try Demo
              </a>
            )}
          </motion.div>

          {/* Purchase Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-4"
          >
            <div className="glass-card rounded-3xl p-6 sticky top-24">
              <h3 className="font-['Unbounded'] text-lg font-bold text-white mb-6">Get This Product</h3>

              {/* One-time price */}
              {product.price > 0 && (
                <div className="mb-4">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-3xl font-black text-white">${product.price.toFixed(2)}</span>
                    <span className="text-[#A0A0AB] text-sm">one-time</span>
                  </div>
                  <button
                    data-testid="buy-one-time"
                    onClick={() => handlePurchase("one_time")}
                    disabled={purchasing}
                    className="neon-btn w-full flex items-center justify-center gap-2 py-4 text-base disabled:opacity-50"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {purchasing ? "Processing..." : "Buy Now"}
                  </button>
                </div>
              )}

              {/* Subscription price */}
              {product.subscription_price > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-white">${product.subscription_price.toFixed(2)}</span>
                    <span className="text-[#A0A0AB] text-sm">/month</span>
                  </div>
                  <button
                    data-testid="buy-subscription"
                    onClick={() => handlePurchase("subscription")}
                    disabled={purchasing}
                    className="neon-btn neon-btn-pink w-full flex items-center justify-center gap-2 py-3 text-sm disabled:opacity-50"
                  >
                    <CreditCard className="w-4 h-4" />
                    {purchasing ? "Processing..." : "Subscribe Monthly"}
                  </button>
                </div>
              )}

              {product.price === 0 && product.subscription_price === 0 && (
                <div className="text-center">
                  <span className="text-[#00F0FF] font-['Unbounded'] text-2xl font-bold">FREE</span>
                  {product.download_url && (
                    <a
                      href={product.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="neon-btn w-full flex items-center justify-center gap-2 py-4 mt-4"
                    >
                      Download Free
                    </a>
                  )}
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center gap-2 text-[#A0A0AB] text-xs">
                  <CreditCard className="w-4 h-4" />
                  Secure payment via Stripe
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16 max-w-3xl">
          <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-2">
            Reviews {product.review_count > 0 && <span className="text-[#A0A0AB] text-base font-normal">({product.review_count})</span>}
          </h2>
          {product.avg_rating > 0 && (
            <div className="flex items-center gap-2 mb-6">
              <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className={`w-5 h-5 ${s <= Math.round(product.avg_rating) ? "text-yellow-400 fill-yellow-400" : "text-white/20"}`} />)}</div>
              <span className="text-white font-bold">{product.avg_rating}</span>
              <span className="text-[#A0A0AB] text-sm">out of 5</span>
            </div>
          )}

          {/* Write Review */}
          <form onSubmit={handleSubmitReview} className="glass-card rounded-2xl p-6 mb-8" data-testid="review-form">
            <h3 className="text-white font-semibold text-sm mb-4">Write a Review</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-[#A0A0AB] mb-1 block">Your Name</label>
                <input data-testid="review-name" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 text-sm" value={reviewForm.reviewer_name} onChange={(e) => setReviewForm({...reviewForm, reviewer_name: e.target.value})} required placeholder="Your name" />
              </div>
              <div>
                <label className="text-xs text-[#A0A0AB] mb-1 block">Rating</label>
                <div className="flex gap-1 mt-1">{[1,2,3,4,5].map(s => (
                  <button key={s} type="button" onClick={() => setReviewForm({...reviewForm, rating: s})} data-testid={`review-star-${s}`}>
                    <Star className={`w-7 h-7 transition-colors ${s <= reviewForm.rating ? "text-yellow-400 fill-yellow-400" : "text-white/20 hover:text-yellow-400/50"}`} />
                  </button>
                ))}</div>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs text-[#A0A0AB] mb-1 block">Comment</label>
              <textarea data-testid="review-comment" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 text-sm resize-none" rows={3} value={reviewForm.comment} onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})} placeholder="Share your experience..." />
            </div>
            <button data-testid="review-submit" type="submit" disabled={reviewSubmitting} className="neon-btn flex items-center gap-2 text-sm py-2 px-6 disabled:opacity-50">
              <Send className="w-4 h-4" /> {reviewSubmitting ? "Submitting..." : "Submit Review"}
            </button>
          </form>

          {/* Reviews List */}
          <div className="space-y-4">
            {reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="glass-card rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#4D00FF] flex items-center justify-center text-white text-xs font-bold">{r.reviewer_name?.charAt(0)?.toUpperCase()}</div>
                    <span className="text-white font-semibold text-sm">{r.reviewer_name}</span>
                  </div>
                  <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= r.rating ? "text-yellow-400 fill-yellow-400" : "text-white/20"}`} />)}</div>
                </div>
                {r.comment && <p className="text-[#A0A0AB] text-sm leading-relaxed">{r.comment}</p>}
                <p className="text-[#A0A0AB]/50 text-xs mt-2 font-['JetBrains_Mono']">{new Date(r.created_at).toLocaleDateString()}</p>
              </div>
            ))}
            {reviews.length === 0 && <p className="text-[#A0A0AB] text-sm text-center py-6">No reviews yet. Be the first!</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
